/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  isNode,
  Controls,
  Background,
  ControlButton,
  ReactFlowProvider
} from 'react-flow-renderer';
import dagre from 'dagre';
import { Tooltip } from 'antd';
import { truncate } from 'lodash';
import { RotateLeftOutlined, RotateRightOutlined } from '@ant-design/icons';
import { defaultTrade } from '../../components/TradeRelationShip';

const NetworkChart3 = ({
  tradeData = defaultTrade,
  module = 'pme',
  isLoading = false,
  selectable = false,
  value = null,
  onChange = () => { },
  uniqueId = null,
}) => {
  const [model, setModel] = useState(value || []);
  const position = { x: 0, y: 0 };
  const dagreGraph = new dagre.graphlib.Graph();
  const [graphDirection, setGraphDirection] = useState('TB')
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  const getLayoutedElements = (elements, direction = 'TB') => {
    const isHorizontal = direction === 'LR';
    dagreGraph.setGraph({ rankdir: direction });
    elements.forEach((el) => {
      if (isNode(el)) {
        dagreGraph.setNode(el.id, isHorizontal ? { width: 300, height: 50 } : { width: 150, height: 75 });
      } else {
        dagreGraph.setEdge(el.source, el.target);
      }
    });
    dagre.layout(dagreGraph);
    return elements.map((el) => {
      if (isNode(el)) {
        const nodeWithPosition = dagreGraph.node(el.id);
        el.targetPosition = isHorizontal ? 'left' : 'top';
        el.sourcePosition = isHorizontal ? 'right' : 'bottom';
        el.position = {
          x: nodeWithPosition.x + Math.random() / 1000,
          y: nodeWithPosition.y,
        };
      }
      return el;
    });
  };

  useEffect(() => {
    if (selectable) {
      onChange(model);
      setElements((els) =>
        els.map(el => {
          if (!el.main) {
            if (model.find(m => m.id === el.id)) {
              el.style = { ...el.style, opacity: '1' };
            } else {
              el.style = { ...el.style, opacity: '0.4' };
            }
          }
          return el;
        })
      );
    }
  }, [model])

  const workAround = () => {
    const fitView = document.querySelector('.react-flow__controls-fitview');
    if (fitView) {
      fitView.click();
    }
  }

  const getNodeData = (nodeId, node, inp = false, isMain = false) => {
    const cfg = {
      id: nodeId,
      main: isMain,
      data: {
        datastakeId: node.datastakeId,
        namespace: node.namespace,
        parent: node.parent,
        selected: !!model.find(m => m.id === node.parent),
        main: isMain,
        label: (
          <>
            <Tooltip title={node.title}>
              <div style={{
                width: '100%',
                minHeight: '25px',
                background: '#fff',
                textAlign: 'start',
                padding: '0 8px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flexStart',
                alignItems: 'center',
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  background: node.color,
                  borderRadius: '2px',
                  marginRight: '4px',
                }}></div>
                <p style={{ margin: '0' }}>{truncate(node.title, { length: 20 })}</p>
              </div>
            </Tooltip>
            <Tooltip title={node.name}>
              <p style={{ color: node.dashed ? node.color : '#fff', fontSize: '14px', margin: '6px 0 6px 8px', width: '100%' }}>
                {truncate(node.name, { length: 18 })}
              </p>
            </Tooltip>
          </>
        )
      },
      position,
      style: {
        border: node.dashed ? `2px dashed ${node.color}` : '1px solid black',
        padding: 0,
        backgroundColor: node.dashed ? '#fff' : node.color,
        textAlign: 'start',
        opacity: selectable && !model.find(m => m.id === node.parent) && !isMain ? '0.4' : '1',
      },
    }
    if (inp) {
      Object.assign(cfg, { type: 'input' });
    }
    return cfg;
  }

  const getLeftSide = (targetId, nodes, ind, all = [], visited = [], links = []) => {
    if (nodes.length === ind) {
      return all;
    }
    const node = nodes[ind];
    const nodeId = node.parent;
    if (nodeId) {
      if (!visited.includes(nodeId)) {
        visited.push(nodeId);
        all.push(getNodeData(nodeId, node, false))
      }
      const linkId = `${targetId}-${nodeId}`;
      if (!links.includes(linkId)) {
        links.push(linkId);
        all.push({
          id: linkId,
          source: nodeId,
          target: targetId,
          arrowHeadType: 'arrowclosed',
          animated: true,
          // type: 'smoothstep',
        });
      }
      if (node.parents && Array.isArray(node.parents) && node.parents.length) {
        all.push(...getLeftSide(nodeId, node.parents, 0, [], visited, links));
      }
    }
    ind += 1;
    return getLeftSide(targetId, nodes, ind, all, visited, links);
  }
  const getRightSide = (sourceId, nodes, ind, all = [], visited = [], links = []) => {
    if (nodes.length === ind) {
      return all;
    }
    const node = nodes[ind];
    const nodeId = node.parent;
    if (nodeId) {
      if (!visited.includes(nodeId)) {
        visited.push(nodeId);
        all.push(getNodeData(nodeId, node, false))
      }
      const linkId = `${nodeId}-${sourceId}`;
      if (!links.includes(linkId)) {
        links.push(linkId);
        all.push({
          id: linkId,
          source: sourceId,
          target: nodeId,
          arrowHeadType: 'arrowclosed',
          animated: true,
          // type: 'smoothstep',
        });
      }
      if (node.children && Array.isArray(node.children) && node.children.length) {
        all.push(...getRightSide(nodeId, node.children, 0, [], visited, links));
      }
    }
    ind += 1;
    return getRightSide(sourceId, nodes, ind, all, visited, links);
  }

  const [elements, setElements] = useState([])
  const [instance, setInstance] = useState(null);

  useEffect(() => {
    if (instance) {
      instance.fitView();
      workAround();
    }
  }, [instance]);

  useEffect(() => {
    const initial = [].concat(...[(() => {
      const tmp = [];
      const mainId = tradeData.parent || `${tradeData.type}1`
      tmp.push(getNodeData(mainId, tradeData, false, true));
      if (tradeData.parents && Array.isArray(tradeData.parents) && tradeData.parents.length) {
        tmp.push(...getLeftSide(mainId, tradeData.parents, 0, [], [mainId]));
      }
      const visited = tmp.filter(d => d.data).map(d => d.id);
      const links = tmp.filter(d => d.source).map(d => d.id);
      if (tradeData.children && Array.isArray(tradeData.children) && tradeData.children.length) {
        tmp.push(...getRightSide(mainId, tradeData.children, 0, [], visited || [], links || []));
      }
      return tmp;
    })()]);
    const layoutedElements = getLayoutedElements(initial, 'TB');
    if ((layoutedElements || []).length === 1 && layoutedElements[0].id === 'undefined1') {
      setElements([]);
    } else {
      setElements(layoutedElements);
    }

    setTimeout(() => {
      if (instance) {
        instance.fitView();
        workAround();
      }
    }, 300)
  }, [tradeData])

  const onLoad = useCallback((inst) => {
    setTimeout(() => {
      inst.fitView();
      workAround();
    }, 300);
    if (!instance) {
      setInstance(inst);
    }
  }, []);

  const onLayout = useCallback(
    (direction) => {
      const layoutedElements = getLayoutedElements(elements, direction);
      setElements(layoutedElements);
      setTimeout(() => {
        if (instance && typeof instance.fitView === 'function') {
          instance.fitView();
          workAround();
        }
      }, 300);
    },
    [elements]
  );

  return <ReactFlowProvider>
    <ReactFlow
      elements={isLoading ? [] : elements}
      onLoad={onLoad}
      onElementClick={!selectable ? (ev, { data: elData }) => {
        if (elData && typeof elData === 'object' && elData.namespace && elData.datastakeId) {
          const win = window.open(`/app/${module}/view/${elData.namespace}/${elData.datastakeId}`, "_blank");
          win.focus();
        }
      } : (ev, { main, data: elData }) => {
        if (!elData.main) {
          if (!model.includes(elData.parent)) {
            setModel(model.concat([elData.parent]));
          } else {
            setModel(model.filter(m => m !== elData.parent));
          }
        }
      }}
      onDoubleClick={() => { }}
      nodesDraggable={false}
      nodesConnectable={false}
      connectionLineType="smoothstep">
      {!selectable ? <Controls>
        <ControlButton onClick={() => {
          if (graphDirection === 'TB') {
            onLayout('LR');
            setGraphDirection('LR');
          } else {
            onLayout('TB');
            setGraphDirection('TB');
          }
        }} > {graphDirection === 'TB' ? <RotateLeftOutlined /> : <RotateRightOutlined />}</ControlButton>
      </Controls> : null}
      <Background color="black" gap={10} />
    </ReactFlow>
  </ReactFlowProvider>
}

export default NetworkChart3;
